// ============================================================================
// AdminEnrollmentRecordExternalPanel — record an externally-executed AMP
// agreement for ONE producer and enroll them (Build S6b, Task #37).
// ============================================================================
// Route: #admin/enrollments/<uuid>/record-external. Sidebar pinned to
// Enrollments. UIF only. The three PDFs upload via the shared two-phase flow
// (enrollment-scoped) BEFORE submit; "Record & Enroll" POSTs their finalized
// document_ids + execution metadata, and the server records the agreement and
// walks the enrollment to `enrolled` in one transaction.
// ============================================================================

import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader2, AlertCircle, ArrowLeft, CheckCircle2, UploadCloud, Plus, Trash2,
} from 'lucide-react';
import {
  api, type AdminEnrollmentDetail, type ExternalExecutionChannel,
  type RecordExternalAgreementPayload, type VocOnFileResult,
} from '../api';
import { uploadDocument } from '../lib/documentUpload';
import { businessToday } from '../lib/businessDate';

interface Props { token: string; enrollmentId: string; }

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const inputCls =
  'w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none';

const CHANNEL_LABELS: Record<ExternalExecutionChannel, string> = {
  external_docusign: 'DocuSign (signed off-platform)',
  external_paper: 'Paper / wet signature',
  external_email: 'Emailed signed PDF',
};

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-gray-500 mt-1">{hint}</span>}
    </label>
  );
}

// One PDF upload slot: pick -> two-phase upload -> holds the finalized doc id.
function FileSlot({
  label, hint, disabled, uploadedId, onUploaded, token, docTitle, farmId, scopeId, typeCode,
}: {
  label: string; hint?: string; disabled?: boolean;
  uploadedId: string | null; onUploaded: (id: string | null) => void;
  token: string; docTitle: string; farmId: number; scopeId: string; typeCode: string;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState('');
  const [err, setErr] = useState('');
  const [name, setName] = useState('');

  async function pick(file: File) {
    setErr(''); setName(file.name);
    try {
      const id = await uploadDocument(token, file, {
        title: docTitle, type_code: typeCode,
        scope_type: 'enrollment', scope_id: scopeId, farm_id: farmId,
      }, setPhase);
      onUploaded(id);
    } catch (e: any) {
      setErr(e?.message ?? 'Upload failed'); onUploaded(null); setName('');
    }
    // No finally { setPhase('') } here: uploadDocument() owns phase cleanup in
    // its own finally as of Copilot #17 r6. Two owners for one piece of state
    // is how it drifts.
  }

  return (
    <div className={'rounded-lg border p-3 ' + (uploadedId ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200')}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-gray-800">{label}</div>
        {uploadedId && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="w-4 h-4" /> uploaded</span>}
      </div>
      {hint && <div className="text-[11px] text-gray-500 mt-0.5">{hint}</div>}
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button" disabled={disabled || !!phase}
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:border-uif-green/60 disabled:opacity-50"
        >
          {phase ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
          {phase || (uploadedId ? 'Replace PDF' : 'Choose PDF')}
        </button>
        {name && <span className="text-xs text-gray-600 truncate max-w-[16rem]">{name}</span>}
      </div>
      <input
        ref={fileRef} type="file" accept=".pdf" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void pick(f); e.target.value = ''; }}
      />
      {err && <div className="mt-1 text-xs text-red-600">{err}</div>}
    </div>
  );
}

export function AdminEnrollmentRecordExternalPanel({ token, enrollmentId }: Props) {
  const [detail, setDetail] = useState<AdminEnrollmentDetail | null>(null);
  const [voc, setVoc] = useState<VocOnFileResult | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [executionDate, setExecutionDate] = useState('');
  const [channel, setChannel] = useState<ExternalExecutionChannel>('external_docusign');
  const [docusignUuid, setDocusignUuid] = useState('');
  // Stable per-signer ids, not array indices (Copilot #17 r1). Rows are
  // deletable, and keying by index makes React reuse the DOM node at that
  // position after a removal — the values visibly jump and an edit can land on
  // the wrong signer. The id is local-only; the payload still sends plain names.
  const nextSignerId = useRef(1);
  const [signers, setSigners] = useState<Array<{ id: number; name: string }>>(
    [{ id: 0, name: '' }],
  );
  const [agreementId, setAgreementId] = useState<string | null>(null);
  const [certId, setCertId] = useState<string | null>(null);
  const [vocId, setVocId] = useState<string | null>(null);
  // Task #45 item 3 (extended 2026-07-19): the VOC waiver, which until now
  // existed only on the batch panel. C5FM and SHUM have no preliminary VOC and
  // their agreements are already uploaded, so batch (which demands a fresh
  // upload) and this panel (which had no waiver) were both closed to them.
  const [vocWaived, setVocWaived] = useState(false);
  const [vocWaiverReason, setVocWaiverReason] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    setLoadErr(null);
    try {
      const [d, v] = await Promise.all([
        api.adminEnrollmentDetail(token, enrollmentId),
        api.adminEnrollmentVocOnFile(token, enrollmentId),
      ]);
      setDetail(d); setVoc(v);
    } catch (err: any) {
      setLoadErr(err?.message ?? 'Could not load enrollment');
    }
  }, [token, enrollmentId]);

  useEffect(() => { void load(); }, [load]);

  const back = () => { window.location.hash = `#admin/enrollments/${enrollmentId}`; };

  if (loadErr) {
    return (
      <div className="space-y-4">
        <BackLink onClick={back} />
        <div className="flex items-center gap-2 text-red-700 bg-red-50 rounded-lg p-4"><AlertCircle className="w-5 h-5 shrink-0" /> {loadErr}</div>
      </div>
    );
  }
  if (!detail || !voc) {
    return <div className="flex items-center gap-2 text-gray-500 p-6"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  }

  const e = detail.enrollment;
  const farmId = e.farm_id;
  const alreadyEnrolled = e.status === 'enrolled';
  const vocRequired = !voc.on_file;
  const certRequired = channel === 'external_docusign';
  // UIF business date (America/Chicago), per David's 2026-07-20 program-wide
  // ruling. Must match the API's guard exactly — using the browser's local day
  // here is what produced 422s for operators ahead of UTC.
  const today = businessToday();

  const cleanSigners = signers.map((s) => s.name.trim()).filter(Boolean);
  const uuidOk = !docusignUuid.trim() || UUID_RE.test(docusignUuid.trim());
  const dateOk = !!executionDate && executionDate <= today;

  // A waived VOC satisfies the leg only WITH a reason — mirrors the batch
  // panel (…BatchPanel.tsx:57-62) and the server's 422
  // (adminExternalEnrollment.ts:138-141), so the UI never sends a request that
  // is guaranteed to be rejected.
  const vocOk = !vocRequired || !!vocId || (vocWaived && !!vocWaiverReason.trim());
  const ready =
    !alreadyEnrolled && dateOk && cleanSigners.length >= 1 && uuidOk &&
    !!agreementId && (!certRequired || !!certId) && vocOk;

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setError(null);
    try {
      const payload: RecordExternalAgreementPayload = {
        execution_date: executionDate,
        execution_channel: channel,
        docusign_envelope_uuid: docusignUuid.trim() || null,
        signer_names: cleanSigners,
        agreement_document_id: agreementId!,
        certificate_document_id: certId,
        // Waiving and attaching are mutually exclusive: when waived there is no
        // VOC document, so this is sent as an explicit null. (The comment
        // previously said the key was "dropped" while the code sent null — the
        // backend treats absent and null identically at
        // adminExternalEnrollment.ts, but the prose was wrong. GPT review.)
        voc_document_id: vocWaived ? null : vocId,
        ...(vocWaived
          ? { voc_waived: true, voc_waiver_reason: vocWaiverReason.trim() }
          : {}),
      };
      await api.adminEnrollmentRecordExternal(token, enrollmentId, payload);
      setDone(true);
    } catch (err: any) {
      setError(err?.message ?? 'Could not record external agreement');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="space-y-4">
        <BackLink onClick={back} />
        <div className="bg-white rounded-xl border border-emerald-200 shadow-sm p-6 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
          <h2 className="mt-3 text-lg font-bold text-gray-900">Recorded & enrolled</h2>
          <p className="mt-1 text-sm text-gray-600">
            <span className="font-mono text-xs text-gray-500">{e.farm_code}</span> {e.farm_name} is now <b>enrolled</b> in {e.program_name}.
          </p>
          <button type="button" onClick={back} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-green-700 rounded-lg hover:bg-green-800">
            View enrollment
          </button>
        </div>
      </div>
    );
  }

  const docTitle = (kind: string) => `${e.farm_code} — ${kind}`;

  return (
    <div className="space-y-4">
      <BackLink onClick={back} />

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              <span className="font-mono text-sm text-gray-500 mr-2">{e.farm_code}</span>{e.farm_name}
            </h2>
            <div className="mt-1 text-sm text-gray-600">
              {e.program_name} · {e.year_no}{e.cohort_tag ? ` · ${e.cohort_tag}` : ''}
            </div>
          </div>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 whitespace-nowrap">{e.status.replace(/_/g, ' ')}</span>
        </div>
        <p className="mt-3 text-sm text-gray-600">Record an AMP agreement executed off-platform and enroll this producer.</p>
      </div>

      {alreadyEnrolled ? (
        <div className="flex items-center gap-2 text-emerald-800 bg-emerald-50 rounded-lg p-4 text-sm">
          <CheckCircle2 className="w-5 h-5 shrink-0" /> This producer is already <b className="mx-1">enrolled</b> — nothing to record.
        </div>
      ) : (
        <>
          {error && (
            <div className="flex items-center gap-2 text-red-700 bg-red-50 rounded-lg p-3 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" /> {error}
            </div>
          )}

          {/* Execution metadata */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3">
            <h3 className="font-semibold text-gray-900">Execution details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Execution date *" hint={dateOk || !executionDate ? undefined : 'Must be today or earlier'}>
                <input type="date" max={today} value={executionDate} onChange={(ev) => setExecutionDate(ev.target.value)} className={inputCls} />
              </Field>
              <Field label="Execution channel *">
                <select value={channel} onChange={(ev) => setChannel(ev.target.value as ExternalExecutionChannel)} className={inputCls}>
                  {(Object.keys(CHANNEL_LABELS) as ExternalExecutionChannel[]).map((c) => (
                    <option key={c} value={c}>{CHANNEL_LABELS[c]}</option>
                  ))}
                </select>
              </Field>
              <Field label="DocuSign envelope UUID" hint={uuidOk ? 'Optional — preserved for audit' : 'Not a valid UUID'}>
                <input value={docusignUuid} onChange={(ev) => setDocusignUuid(ev.target.value)} placeholder="00000000-0000-0000-0000-000000000000"
                  className={inputCls + (uuidOk ? '' : ' border-red-400')} />
              </Field>
            </div>

            <div>
              <span className="block text-xs font-medium text-gray-700 mb-1">Signer name(s) *</span>
              <div className="space-y-2">
                {signers.map((s) => (
                  <div key={s.id} className="flex items-center gap-2">
                    <input value={s.name}
                      onChange={(ev) => setSigners((prev) => prev.map((p) => (p.id === s.id ? { ...p, name: ev.target.value } : p)))}
                      placeholder="Full legal name" className={inputCls} />
                    {signers.length > 1 && (
                      <button type="button" onClick={() => setSigners((prev) => prev.filter((p) => p.id !== s.id))}
                        className="text-gray-400 hover:text-red-600" aria-label="Remove signer"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => setSigners((prev) => [...prev, { id: nextSignerId.current++, name: '' }])}
                className="mt-2 inline-flex items-center gap-1 text-xs text-green-700 hover:text-green-900"><Plus className="w-3.5 h-3.5" /> Add signer</button>
            </div>
          </div>

          {/* Documents */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3">
            <h3 className="font-semibold text-gray-900">Documents</h3>
            <FileSlot token={token} label="Executed AMP agreement (PDF) *" uploadedId={agreementId} onUploaded={setAgreementId}
              docTitle={docTitle('Executed AMP agreement')} farmId={farmId} scopeId={enrollmentId} typeCode="AMP_AGREEMENT" />
            <FileSlot token={token} label="Execution certificate (PDF)"
              hint={certRequired ? 'Required for DocuSign executions' : 'Optional for this channel'}
              uploadedId={certId} onUploaded={setCertId}
              docTitle={docTitle('Execution certificate')} farmId={farmId} scopeId={enrollmentId} typeCode="EXECUTION_CERTIFICATE" />
            {/* While waived the slot reports "not sent" rather than showing a
                green uploaded state, because the payload deliberately sends
                voc_document_id: null. The finalized vocId is DELIBERATELY NOT
                cleared (Copilot #17 r9 proposed clearing it; rejected): the ID
                cannot leak — the waived payload sends null regardless — and
                discarding it would abandon an already-finalized upload, forcing
                a re-upload if the operator un-ticks the waiver. Presentation
                fixed, state preserved. */}
            <FileSlot token={token} label="VOC assessment (PDF)"
              hint={
                vocWaived
                  ? (vocId
                      ? 'Waived — the uploaded VOC will NOT be attached to this record'
                      : 'Waived — no upload required')
                  : vocRequired ? 'No VOC on file for this farm — upload required'
                    : 'Already on file for this farm'
              }
              disabled={!vocRequired || vocWaived}
              uploadedId={vocWaived ? null : vocId} onUploaded={setVocId}
              docTitle={docTitle('VOC assessment')} farmId={farmId} scopeId={enrollmentId} typeCode="VOC_ASSESSMENT" />
          </div>

          {/* VOC waiver — offered only when the farm has no VOC on file, since
              waiving an existing VOC is meaningless. Copy and behaviour mirror
              the batch panel so the two recording surfaces read identically. */}
          {vocRequired && (
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-2 text-xs text-gray-700">
                <input type="checkbox" checked={vocWaived}
                  onChange={(ev) => {
                    setVocWaived(ev.target.checked);
                    if (!ev.target.checked) setVocWaiverReason('');
                  }}
                  className="rounded border-gray-300" />
                Waive VOC for this producer
              </label>
              <input
                value={vocWaiverReason}
                disabled={!vocWaived}
                onChange={(ev) => setVocWaiverReason(ev.target.value)}
                placeholder="Reason (required when waiving)"
                className={inputCls + ' flex-1 min-w-[16rem] disabled:bg-gray-50 disabled:text-gray-400'
                  + (vocWaived && !vocWaiverReason.trim() ? ' border-amber-400' : '')} />
              {vocWaived && (
                <span className="text-xs text-amber-700">
                  VOC stays outstanding for this farm — recorded as waived, not satisfied.
                </span>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <button type="button" disabled={!ready || busy} onClick={() => void submit()}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-green-700 rounded-lg hover:bg-green-800 disabled:opacity-50">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Record &amp; Enroll
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
      <ArrowLeft className="w-4 h-4" /> Back to enrollment
    </button>
  );
}
